import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, map, take, switchMap, of } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Guard that protects admin-only routes.
 * 
 * Security approach:
 * 1. First checks if user is authenticated (has token)
 * 2. Refreshes user data from server to validate role
 * 3. Only allows access if user has admin or super_admin role
 * 
 * This ensures that even if localStorage is manipulated,
 * the server-side role check will catch unauthorized access.
 */
@Injectable({
  providedIn: 'root',
})
export class AdminGuard implements CanActivate {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const token = this.authService.getToken();
    
    if (!token) {
      return of(this.router.createUrlTree(['/login']));
    }

    // Always refresh user from server to validate role
    return this.authService.refreshUser().pipe(
      take(1),
      map((user) => {
        const role = user?.role;
        const isAdmin = role === 'admin' || role === 'super_admin';
        
        if (isAdmin) {
          return true;
        }
        
        // Redirect to forbidden page if not admin
        return this.router.createUrlTree(['/forbidden']);
      })
    );
  }
}
